import * as THREE from 'three';
import { lightShare } from '../../sim/light';
import { hash3 } from '../fishSwim';
import type { HourSun } from '../sunlight';

/**
 * Light shafts (M169, the Reef's redesign v4, its brief's "occasional light shafts in sufficiently shallow water",
 * "Avoid making the entire scene glow", "avoid excessive transparent planes"): a few soft columns of the sun's light
 * from the surface down, each a quad turned about its own axis to face the camera, added to what is behind it and
 * writing no depth, drawn before the animals. Each a rendering choice (JUDGED unless said):
 *
 * - `count` of them, in the box `x` by `z` about the reef, each `widthM` across, reaching `downM` under the surface.
 * - Slanted as the sun's beam is in the water, refracted at the surface (Snell's law, the water's index `INDEX`, 1.33,
 *   so no beam lies more than 48.8° off the vertical); none by night or by the moon.
 * - As bright as `strength` of the light reaching each depth on the day's clarity (`lightShare`), the sun's beam's
 *   share of noon's (`HourSun.level`), fading to none at the bottom of each and with the camera's distance (`fadeM`),
 *   and each swaying in its brightness slowly (`swayS`, the surface's swell passing over) and flickering a little
 *   faster (`shimmer` of it either way over `shimmerS`, the shorter waves refocusing the light: M173, the particles'
 *   brief's "Gentle variations in brightness due to surface wave movement").
 */
export const SHAFTS = { count: 7, x: [-12, 26], z: [-26, 26], widthM: [1.2, 2.8], downM: 14, strength: 0.26, fadeM: [12, 34], swayS: [7, 13], shimmer: 0.12, shimmerS: [2.3, 3.9] } as const;

/** Water's refractive index for light (Snell's law at the surface). */
const INDEX = 1.33;

/** `update`'s scratch, set afresh each frame (M182: made each frame before). */
const UP = new THREE.Vector3();
const TO_EYE = new THREE.Vector3();
const SIDE = new THREE.Vector3();
const FACING = new THREE.Vector3();
const BASIS = new THREE.Matrix4();

/** A shaft's picture: bright at its top fading to none at its foot, soft at its two sides. */
function shaftTexture(): THREE.DataTexture {
  const [w, h] = [16, 64];
  const rgba = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const across = 1 - Math.abs((x + 0.5) / w - 0.5) * 2;
      const down = 1 - y / (h - 1);
      rgba.set([255, 255, 255, Math.round(255 * Math.sin((Math.PI / 2) * across) ** 2 * down ** 1.5)], (y * w + x) * 4);
    }
  }
  const t = new THREE.DataTexture(rgba, w, h);
  t.magFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}

export class LightShafts {
  readonly group = new THREE.Group();
  private readonly shafts: { mesh: THREE.Mesh; top: THREE.Vector3; phase: number; swayS: number; shimmerS: number; lit: number }[] = [];
  private readonly texture = shaftTexture();
  private readonly geometry: THREE.PlaneGeometry;
  private bright = 0;
  private seconds = 0;
  private readonly slant = new THREE.Vector3(0, -1, 0);
  /** Each shaft's beam as last given (`beams`), kept so none is made a frame. */
  private readonly beamsNow: { top: THREE.Vector3; halfWidthM: number; bright: number }[];

  constructor(seed: number) {
    const s = SHAFTS;
    // A quad from its top down `downM`, its axis along its own y.
    this.geometry = new THREE.PlaneGeometry(1, s.downM);
    this.geometry.translate(0, -s.downM / 2, 0);
    for (let k = 0; k < s.count; k++) {
      const material = new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: 0xfff6dc, opacity: 0 });
      const mesh = new THREE.Mesh(this.geometry, material);
      mesh.scale.x = s.widthM[0] + (s.widthM[1] - s.widthM[0]) * hash3(seed, k, 191);
      // After the rock and the colonies, whose depth hides them, and before the animals, which write none (M169's review:
      // drawn after them, a fish in front of a shaft had its light laid over it).
      mesh.renderOrder = 3.2;
      mesh.frustumCulled = false;
      mesh.raycast = () => undefined;
      const top = new THREE.Vector3(s.x[0] + (s.x[1] - s.x[0]) * hash3(seed, k, 192), -0.05, s.z[0] + (s.z[1] - s.z[0]) * hash3(seed, k, 193));
      this.shafts.push({ mesh, top, phase: hash3(seed, k, 194), swayS: s.swayS[0] + (s.swayS[1] - s.swayS[0]) * hash3(seed, k, 195), shimmerS: s.shimmerS[0] + (s.shimmerS[1] - s.shimmerS[0]) * hash3(seed, k, 196), lit: 0 });
      this.group.add(mesh);
    }
    this.beamsNow = this.shafts.map((s) => ({ top: s.top, halfWidthM: s.mesh.scale.x / 2, bright: 0 }));
  }

  /** The sun at an hour: its beam's slant in the water and its brightness at the shafts' middle depth; none by night. */
  setLight(sun: HourSun): void {
    const [dx, dy, dz] = sun.direction;
    if (sun.beam <= 0 || dy <= 0) {
      this.bright = 0;
      return;
    }
    // Refracted: the beam's angle off the vertical in the water, its sine the air's over the index, the same way across.
    const across = Math.hypot(dx, dz);
    const sinWater = across / INDEX;
    const cosWater = Math.sqrt(1 - sinWater * sinWater);
    // The light comes down from the sun's side, so the shaft runs away from it.
    this.slant.set(across > 0 ? (-dx / across) * sinWater : 0, -cosWater, across > 0 ? (-dz / across) * sinWater : 0);
    this.bright = SHAFTS.strength * Math.min(1, sun.level) * lightShare(sun.kdPar, SHAFTS.downM / 2);
  }

  /** Each shaft turned to face the camera about its own axis, its brightness swaying, fading with the camera's distance. */
  update(camera: THREE.Camera, seconds: number, calm: boolean): void {
    if (!calm) this.seconds = (this.seconds + seconds) % 3600;
    UP.copy(this.slant).negate();
    for (const s of this.shafts) {
      const material = s.mesh.material as THREE.MeshBasicMaterial;
      TO_EYE.copy(camera.position).sub(s.top);
      const distance = TO_EYE.length();
      const near = 1 - Math.min(1, Math.max(0, (distance - SHAFTS.fadeM[0]) / (SHAFTS.fadeM[1] - SHAFTS.fadeM[0])));
      const sway = (0.65 + 0.35 * Math.sin(2 * Math.PI * (this.seconds / s.swayS + s.phase))) * (1 + SHAFTS.shimmer * Math.sin(2 * Math.PI * (this.seconds / s.shimmerS + 2 * s.phase)));
      s.lit = this.bright * sway;
      material.opacity = s.lit * near;
      s.mesh.visible = material.opacity > 0.002;
      if (!s.mesh.visible) continue;
      // Its axis along the beam, its face toward the camera as near as that allows.
      SIDE.crossVectors(UP, TO_EYE).normalize();
      FACING.crossVectors(SIDE, UP).normalize();
      BASIS.makeBasis(SIDE, UP, FACING);
      s.mesh.quaternion.setFromRotationMatrix(BASIS);
      s.mesh.position.copy(s.top);
    }
  }

  /**
   * Each shaft's beam as the water's particles are lit by it (M173): its top, half its width and its brightness now
   * (its sway's, not its fade's with the camera's distance), the same objects each frame.
   */
  beams(): readonly { top: THREE.Vector3; halfWidthM: number; bright: number }[] {
    for (let k = 0; k < this.shafts.length; k++) (this.beamsNow[k] as { bright: number }).bright = (this.shafts[k] as { lit: number }).lit;
    return this.beamsNow;
  }

  /** The way the sun's beam runs down through the water now, a unit vector (`setLight`). */
  slantNow(): THREE.Vector3 {
    return this.slant;
  }

  /** How bright the shafts are drawn now, the strongest's opacity, for the tests. */
  brightness(): number {
    return this.bright;
  }

  dispose(): void {
    this.geometry.dispose();
    this.texture.dispose();
    for (const s of this.shafts) (s.mesh.material as THREE.Material).dispose();
  }
}
